import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../Config/apiClient';
import { API_ENDPOINTS } from '../Config/api';
import CountUpAnimation from './UI/CountUpAnimation';
import { 
  FiShield, 
  FiUsers, 
  FiBook, 
  FiAlertTriangle,
  FiTrendingUp,
  FiTrendingDown,
  FiMinus,
  FiRefreshCw,
  FiAward
} from 'react-icons/fi';
import { AlertTriangle, Mail } from 'lucide-react';
import AwarenessDepartmentSummary from './Awareness/AwarenessDepartmentSummary';

/**
 * Card completa per la Dashboard Awareness con layout ispirato all'immagine.
 * Mostra health score e metriche phishing/training.
 */
export default function AwarenessDashboardCard({ companyId = null, showDetails = true, onOpenDepartments = null }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [usageMetrics, setUsageMetrics] = useState({
    seatsUsed: 0,
    seatsTotal: 0,
    effectiveActive: 0,
  });

  useEffect(() => {
    fetchDashboard();
  }, [companyId]);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = companyId 
        ? API_ENDPOINTS.awarenessDashboardCompany(companyId)
        : API_ENDPOINTS.awarenessDashboard;
      const [dashboardResult, usersResult, seatsResult] = await Promise.allSettled([
        apiClient.get(url),
        apiClient.get(API_ENDPOINTS.awarenessUsers),
        apiClient.get(`${API_ENDPOINTS.awarenessUsers}seats_info/`),
      ]);

      if (dashboardResult.status !== 'fulfilled') {
        throw dashboardResult.reason;
      }

      setData(dashboardResult.value.data);

      const users = usersResult.status === 'fulfilled' && Array.isArray(usersResult.value.data)
        ? usersResult.value.data
        : [];
      const seatsData = seatsResult.status === 'fulfilled' ? seatsResult.value.data || {} : {};

      const effectiveActive = users.filter((user) => {
        const status = String(user?.account_status || '').toLowerCase();
        if (status === 'attivo' || status === 'active') {
          return true;
        }
        if (status) {
          return false;
        }
        if (user?.is_email_verified === true) {
          return true;
        }
        return false;
      }).length;

      const seatsUsed = Number.isFinite(seatsData?.used) ? seatsData.used : users.length;
      const seatsTotal = Number.isFinite(seatsData?.awareness_seats) ? seatsData.awareness_seats : 0;

      setUsageMetrics({
        seatsUsed,
        seatsTotal,
        effectiveActive,
      });
    } catch (err) {
      console.error('Error fetching awareness dashboard:', err);
      setError(err.response?.data?.message || t('awarenessDashboard.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  // Determina il colore dello status
  const getStatusColor = (status) => {
    switch (status) {
      case 'good':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-800 bg-yellow-400'; // Giallo più intenso come Asight
      case 'critical':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  // Icona trend
  const TrendIcon = ({ trend }) => {
    if (trend === 'improving') return <FiTrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'worsening') return <FiTrendingDown className="w-4 h-4 text-red-500" />;
    return <FiMinus className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="bg-white border rounded-lg p-6 h-full w-full flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 bg-gray-200 rounded-full"></div>
          <div className="h-4 bg-gray-200 rounded w-32"></div>
          <div className="h-3 bg-gray-200 rounded w-24"></div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-white border rounded-lg p-6 h-full w-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <FiAlertTriangle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
          <p>{error || t('awarenessDashboard.noData')}</p>
          <button 
            onClick={fetchDashboard}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 mx-auto"
          >
            <FiRefreshCw className="w-4 h-4" /> {t('awarenessDashboard.retry')}
          </button>
        </div>
      </div>
    );
  }

  // Mapping dei campi API: supporta sia il formato vecchio che quello nuovo dell'API Awareness Platform
  const summary = data.executive_summary || {};
  const contract = data.contract_status || {};
  // L'API restituisce phishing_stats, ma supportiamo anche phishing per retrocompatibilità
  const phishing = data.phishing_stats || data.phishing || {};
  const training = data.training || {};
  const risk = data.risk_analysis || data.risk_score || {};
  const campaigns = data.campaigns || {};
  // Riepilogo per reparto: già incluso nel payload della dashboard,
  // nessuna chiamata aggiuntiva e nessuna heatmap in questa schermata.
  const departmentSummary = data.awareness_by_department || null;
  const seatsUsed = usageMetrics.seatsUsed || summary.active_users || 0;
  const seatsTotal = usageMetrics.seatsTotal || contract.max_users || 0;
  const effectiveActive = usageMetrics.effectiveActive || 0;

  return (
    <div className="font-inter space-y-4 w-full">
      {/* Header Risk Assessment - Tema giallo sfumato Asight */}
      <div className="bg-gradient-to-br from-yellow-300/80 via-yellow-400/70 to-amber-400/80 rounded-lg p-4 text-gray-900 relative overflow-hidden backdrop-blur-sm">
        <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-200/20 rounded-full -mr-12 -mt-12"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-50/40 to-transparent"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold mb-1 text-gray-800">{t('awarenessDashboard.riskScore')}</h2>
              <p className="text-xs text-gray-600 mb-2">
                {t('awarenessDashboard.riskDescription')}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {/* Score principale */}
              <div className="text-right">
                <div className="text-3xl font-black text-gray-800">
                  {(risk.current || summary.risk_score || 0).toFixed(1)}%
                </div>
                <div className="text-xs text-gray-600">{t('awarenessDashboard.riskScore')}</div>
              </div>
              {/* Badge e status */}
              <div className="flex flex-col gap-1">
                <span className="px-3 py-1 rounded-md text-xs font-bold text-amber-900 bg-gradient-to-r from-yellow-200 to-amber-300 shadow-sm">
                  <AlertTriangle size={12} className="inline" /> {t('awarenessDashboard.medium')}
                </span>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <TrendIcon trend={summary.key_metric_trend} />
                  <span>{t('awarenessDashboard.stable')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showDetails && (
        <>
          {/* 4 KPI Cards in grid - Stile pulito come nell'immagine */}
          <div className="grid grid-cols-4 gap-3">
            {/* Utenti attivi */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                  <FiUsers className="w-4 h-4 text-blue-600" />
                </div>
                <div className="text-xs text-gray-500">{t('awarenessDashboard.seatsInserted')}</div>
              </div>
              <div className="text-xl font-bold text-gray-900 mb-1">
                <CountUpAnimation end={seatsUsed} />/<CountUpAnimation end={seatsTotal} />
              </div>
              <div className="text-xs text-gray-500">
                {seatsTotal ? ((seatsUsed || 0) / seatsTotal * 100).toFixed(1) : '0.0'}% {t('awarenessDashboard.active')}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {t('awarenessDashboard.effectiveActiveUsers')}: <strong>{effectiveActive}</strong>/{seatsUsed || 0}
              </div>
            </div>

            {/* Tasso vulnerabilità */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                  <FiShield className="w-4 h-4 text-green-600" />
                </div>
                <div className="text-xs text-gray-500">{t('awarenessDashboard.vulnerabilityRate')}</div>
              </div>
              <div className="text-xl font-bold text-gray-900 mb-1">
                {(phishing.click_rate || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500">{t('awarenessDashboard.phishingClicks')}</div>
            </div>

            {/* Corsi completati */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-emerald-100 rounded-md flex items-center justify-center">
                  <FiBook className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="text-xs text-gray-500">{t('awarenessDashboard.coursesCompleted')}</div>
              </div>
              <div className="text-xl font-bold text-gray-900 mb-1">
                {(training.completion_rate || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500">
                (<CountUpAnimation end={training.courses_completed || 0} /> {t('awarenessDashboard.ofTotal')} <CountUpAnimation end={training.courses_target || campaigns.total || 0} /> {t('awarenessDashboard.courses')})
              </div>
            </div>

            {/* Punteggio medio quiz */}
            <div className="bg-white rounded-lg p-3 border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                  <FiTrendingUp className="w-4 h-4 text-purple-600" />
                </div>
                <div className="text-xs text-gray-500">{t('awarenessDashboard.avgQuizScore')}</div>
              </div>
              <div className="text-xl font-bold text-gray-900 mb-1">
                {(training.average_score || training.average_quiz_score || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500">{t('awarenessDashboard.avgScore')}</div>
            </div>
          </div>

          {/* Riepilogo awareness per reparto */}
          <AwarenessDepartmentSummary data={departmentSummary} onOpenDetail={onOpenDepartments} />

          {/* Sezione Statistiche Phishing */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 bg-yellow-100 rounded-md flex items-center justify-center">
                <FiAlertTriangle className="w-3 h-3 text-yellow-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900"><Mail size={16} className="inline" /> {t('awarenessDashboard.phishingStatsTitle')}</h3>
            </div>
            <div className="grid grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-1">
                  <CountUpAnimation end={phishing.total_emails_sent || 0} />
                </div>
                <div className="text-sm text-gray-500">{t('awarenessDashboard.emailsSent')}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  <CountUpAnimation end={phishing.total_emails_opened || 0} />
                </div>
                <div className="text-xs text-gray-500">{t('awarenessDashboard.opened')} ({(phishing.open_rate || 0).toFixed(1)}%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600 mb-1">
                  <CountUpAnimation end={phishing.total_links_clicked || 0} />
                </div>
                <div className="text-xs text-gray-500">{t('awarenessDashboard.clicks')} ({(phishing.click_rate || 0).toFixed(1)}%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600 mb-1">
                  <CountUpAnimation end={phishing.total_credentials_submitted || 0} />
                </div>
                <div className="text-xs text-gray-500">{t('awarenessDashboard.credentials')} ({(phishing.credential_rate || 0).toFixed(1)}%)</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600 mb-1">
                  <CountUpAnimation end={phishing.total_reported || 0} />
                </div>
                <div className="text-xs text-gray-500">{t('awarenessDashboard.reported')} ({(phishing.report_rate || 0).toFixed(1)}%)</div>
              </div>
            </div>
          </div>

          {/* ========== STATISTICHE FORMAZIONE ========== */}
          {training && (
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FiAward className="text-green-500" />
                {t('awarenessTrainingStats.title')}
              </h2>
              
              {/* Metriche del percorso: i 18 "corsi" sono i moduli di un unico percorso */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-800">{training.users_enrolled || 0}</div>
                  <div className="text-sm text-gray-500">{t('awarenessTrainingStats.usersEnrolled')}</div>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg" title={t('awarenessTrainingStats.fullCompletionHint')}>
                  <div className="text-2xl font-bold text-emerald-600">{training.users_full_completion || 0}</div>
                  <div className="text-sm text-gray-500">{t('awarenessTrainingStats.fullCompletionUsers')}</div>
                </div>
                <div className="text-center p-3 bg-indigo-50 rounded-lg">
                  <div className="text-2xl font-bold text-indigo-600">{training.total_courses_available || 0}</div>
                  <div className="text-sm text-gray-500">{t('awarenessTrainingStats.coursesAvailable')}</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{(training.completion_rate || 0).toFixed(1)}%</div>
                  <div className="text-sm text-gray-500">{t('awarenessTrainingStats.completion')}</div>
                </div>
              </div>
              
              {/* Barra Progresso Completamento Globale */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">{t('awarenessTrainingStats.globalCompletionRate')}</span>
                  <span className="text-sm font-bold text-gray-800">{(training.completion_rate || 0).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${training.completion_rate || 0}%` }}
                  ></div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {t('awarenessTrainingStats.coursesCompletedOf', { completed: training.courses_completed || 0, total: training.courses_target || 0, users: training.users_enrolled || 0, courses: training.total_courses_available || 0 })}
                </div>
              </div>

              {/* Tabella Breakdown per Modulo */}
              {training.course_breakdown && training.course_breakdown.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-700 mb-3">{t('awarenessTrainingStats.perCourse')}</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-4 text-sm font-medium text-gray-600">{t('awarenessTrainingStats.course')}</th>
                          <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">{t('awarenessTrainingStats.enrolled')}</th>
                          <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">{t('awarenessTrainingStats.completed')}</th>
                          <th className="text-center py-2 px-4 text-sm font-medium text-gray-600">{t('awarenessTrainingStats.avgProgress')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {training.course_breakdown.map((course, index) => (
                          <tr key={index} className="border-b border-gray-100">
                            <td className="py-2 px-4 text-sm text-gray-800">
                              {course.course_name || t('awarenessTrainingStats.unknownCourse')}
                            </td>
                            <td className="py-2 px-4 text-sm text-gray-600 text-center">{course.users_enrolled || 0}</td>
                            <td className="py-2 px-4 text-sm text-gray-600 text-center">{course.users_completed || 0}</td>
                            <td className="py-2 px-4 text-sm text-center">
                              {course.avg_progress !== undefined ? (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  course.avg_progress >= 80 ? 'bg-green-100 text-green-700' :
                                  course.avg_progress >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {course.avg_progress.toFixed(1)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}